import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'
import * as shared from '../shared'

// TODO: This should probably be centralized in @attain-apps/entities
const ORG_DIMENSION_ID = ents.core.dms.models.Dimension.Id.from(
  ents.extra.quiver.resources.data.dimensions.organization.RESOURCE.payload,
)

/**
 * Applies one functional usage across the Space records selected in the
 * records list.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.select.single.Component,
    ds.layout.widget.Component,
    ds.notice.Component,
    ds.overlay.centered.Component,
    shared.functionalizations.Component,
  ],
  selector: 'atn-space-bulk-functional-usage-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.full.records.Collection} */
  public readonly recordsEc = ngCore.inject(state.space.full.records.Collection)

  /** @see {@link state.space.full.records.plugins.bulkFunctionalUsage.Plugin} */
  public readonly bulkFunctionalUsagePlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.bulkFunctionalUsage.Plugin)
    .get()

  /** @see {@link state.space.full.serviceCenters.Collection} */
  private readonly _serviceCentersEc = ngCore.inject(
    state.space.full.serviceCenters.Collection,
  )

  /** @see {@link state.space.organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(
    state.space.organizations.Feature,
  )

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** @see {@link ents.core.display.Style} */
  public readonly DisplayStyle = ents.core.display.Style

  /** Entity collection for occupier organizations */
  private readonly _occupierOrganizationsEcSg = ngCore.computed(
    (schema = this._schemaFeature.selectors.schema.asSignal()) => {
      const leafEntity = schema.tryGetLeafEntityOf(ORG_DIMENSION_ID)
      return leafEntity
        ? this._organizationsFeature.selectors
            .getOrganizationEc(leafEntity.id)
            .asSignal()
        : undefined
    },
  )

  /** @see {@link state.space.organizations.plugins.jointUse.Plugin} */
  public readonly jointUsePluginSg = ngCore.computed(
    (organizationsEc = this._occupierOrganizationsEcSg()) =>
      organizationsEc?.plugins
        .lookup(state.space.organizations.plugins.jointUse.Plugin)
        .tryGet(),
  )

  /** Records the functional usage will be applied to */
  public readonly selectedRecordsSg = ngCore.computed(() =>
    this.recordsEc.plugins.selectionPlugin.feature.selectors
      .getAllSelected({
        target: state.space.full.records.SPACE_RECORDS_SELECTION_TARGET,
      })
      .asSignal()
      .filter(TypeChecker.isNotNullish.raw),
  )

  /** Modal title, naming how many records the functional usage applies to */
  public readonly titleSg = ngCore.computed(
    (
      recordCount = this.recordsEc.plugins.selectionPlugin.feature.selectors
        .getAllSelectedIds({
          target: state.space.full.records.SPACE_RECORDS_SELECTION_TARGET,
        })
        .asSignal().length,
    ) =>
      `Update Functional Usage (${recordCount} Space ${
        recordCount === 1 ? 'Record' : 'Records'
      } selected)`,
  )

  /**
   * Methodology items for select.
   *
   * Auto Lab Based is left out: it is only valid for Lab Service records, and
   * a bulk selection cannot be relied on to hold only those.
   */
  public readonly methodologyItemsSg = ngCore.computed<
    ds.input.select.models.Item<ents.extra.space.Record.FunctionalUsage.Methodology>[]
  >(() =>
    ents.extra.space.Record.FunctionalUsage.Methodology.ALL.filter(
      methodology =>
        methodology !==
        ents.extra.space.Record.FunctionalUsage.Methodology.AutoLabBased,
    ).map(methodology => ({ label: methodology, value: methodology })),
  )

  /** Whether the Manual methodology is selected */
  public readonly isManualMethodologySg = ngCore.computed(
    (
      methodology = this.bulkFunctionalUsagePlugin.selectors.methodology.asSignal(),
    ) =>
      methodology ===
      ents.extra.space.Record.FunctionalUsage.Methodology.Manual,
  )

  /** Whether the Service Center methodology is selected */
  public readonly isServiceCenterMethodologySg = ngCore.computed(
    (
      methodology = this.bulkFunctionalUsagePlugin.selectors.methodology.asSignal(),
    ) =>
      methodology ===
      ents.extra.space.Record.FunctionalUsage.Methodology.ServiceCenter,
  )

  /** Whether the Joint Use methodology is selected */
  public readonly isJointUseMethodologySg = ngCore.computed(
    (
      methodology = this.bulkFunctionalUsagePlugin.selectors.methodology.asSignal(),
    ) =>
      methodology ===
      ents.extra.space.Record.FunctionalUsage.Methodology.JointUse,
  )

  /** Items for the service center dropdown */
  public readonly serviceCenterItemsSg = ngCore.computed<
    ds.input.select.models.Item<ents.extra.space.ServiceCenter.Id>[]
  >(
    (
      serviceCenters = this._serviceCentersEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) =>
      serviceCenters.map(serviceCenter => ({
        label: serviceCenter.name,
        value: serviceCenter.id,
      })),
  )

  /** Allocations of the selected service center, shown readonly */
  public readonly serviceCenterFunctionalizationsSg = ngCore.computed(
    (
      serviceCenterId = this.bulkFunctionalUsagePlugin.selectors.serviceCenterId.asSignal(),
      serviceCenter = serviceCenterId
        ? this._serviceCentersEc.plugins.cachePlugin.feature.selectors
            .getCachedEntity(serviceCenterId)
            .asSignal()
        : undefined,
    ) => serviceCenter?.functionalizations ?? [],
  )

  /** Whether any selected record has no occupier organization */
  public readonly hasRecordWithoutOccupierSg = ngCore.computed(
    (selectedRecords = this.selectedRecordsSg()) =>
      selectedRecords.some(record => TypeChecker.isNullish(record.occupierId)),
  )

  /**
   * Occupier shared by every selected record, if they all have the same one.
   *
   * Joint Use allocations are calculated per organization, so they can only be
   * previewed when the whole selection resolves to a single occupier.
   */
  public readonly sharedOccupierIdSg = ngCore.computed(
    (selectedRecords = this.selectedRecordsSg()) => {
      const occupierIds = new Set(
        selectedRecords.map(record => record.occupierId),
      )
      const [occupierId] = occupierIds
      return occupierIds.size === 1 ? occupierId : undefined
    },
  )

  /** Whether the Save button should be enabled */
  public readonly canConfirmSg = ngCore.computed(
    (
      methodology = this.bulkFunctionalUsagePlugin.selectors.methodology.asSignal(),
      functionalizations = this.bulkFunctionalUsagePlugin.selectors.functionalizations.asSignal(),
      serviceCenterId = this.bulkFunctionalUsagePlugin.selectors.serviceCenterId.asSignal(),
      hasRecordWithoutOccupier = this.hasRecordWithoutOccupierSg(),
    ) => {
      if (TypeChecker.isNullish(methodology)) {
        return false
      }

      switch (methodology) {
        case ents.extra.space.Record.FunctionalUsage.Methodology.ServiceCenter:
          return TypeChecker.isNotNullish(serviceCenterId)
        case ents.extra.space.Record.FunctionalUsage.Methodology.JointUse:
          // Joint Use is calculated from the occupier, so a record without one
          // has nothing to calculate from.
          return !hasRecordWithoutOccupier
        case ents.extra.space.Record.FunctionalUsage.Methodology.Manual:
          return (
            ents.extra.space.Functionalization.isEmpty(functionalizations) ||
            ents.extra.space.Functionalization.isFullyCoded(functionalizations)
          )
        case ents.extra.space.Record.FunctionalUsage.Methodology.Vacant:
        case ents.extra.space.Record.FunctionalUsage.Methodology.Unassignable:
          // Neither carries allocations of its own.
          return true
        case ents.extra.space.Record.FunctionalUsage.Methodology.AutoLabBased:
          // Not offered in bulk, since it is only valid for Lab Service
          // records and a bulk selection may hold anything.
          return false
        default:
          throw TypeAsserter.assertIsNever(methodology)
      }
    },
  )

  /**
   * Loads the Joint Use allocations of the shared occupier, so the modal can
   * preview what the selected records will end up with.
   */
  private readonly _fetchJointUseFunctionalizations = ngCore.effect(() => {
    const isJointUse = this.isJointUseMethodologySg()
    const occupierId = this.sharedOccupierIdSg()
    const jointUsePlugin = this.jointUsePluginSg()

    if (TypeChecker.isNullish(jointUsePlugin)) {
      return
    }

    if (isJointUse && TypeChecker.isNotNullish(occupierId)) {
      jointUsePlugin.feature.actions.setCurrentEntityTarget.request
        .from(
          'Space.RecordsList.BulkFunctionalUsageModal.JointUseMethodologySelected',
        )
        .dispatch({ id: occupierId })
    } else {
      jointUsePlugin.feature.actions.releaseCurrentEntityTarget.request
        .from(
          'Space.RecordsList.BulkFunctionalUsageModal.JointUseMethodologyLeft',
        )
        .dispatch()
    }
  })

  /** Stores the edited methodology */
  public updateFunctionalizationMethodology(
    functionalizationMethodology: string | undefined,
  ): void {
    ents.extra.space.Record.FunctionalUsage.Methodology.assertIsMethodology(
      functionalizationMethodology,
    )
    this.bulkFunctionalUsagePlugin.actions.setMethodology
      .from('Space.RecordsList.BulkFunctionalUsageModal.MethodologyChanged')
      .dispatch(functionalizationMethodology)
  }

  /** Stores the edited service center */
  public updateServiceCenterHandler(
    serviceCenterId: ents.extra.space.ServiceCenter.Id | undefined,
  ): void {
    this.bulkFunctionalUsagePlugin.actions.setServiceCenter
      .from('Space.RecordsList.BulkFunctionalUsageModal.ServiceCenterChanged')
      .dispatch(serviceCenterId)
  }

  /** Stores one edited allocation */
  public updateFunctionalization(
    functionalization: ents.extra.space.Functionalization,
  ): void {
    this.bulkFunctionalUsagePlugin.actions.updateFunctionalization
      .from(
        'Space.RecordsList.BulkFunctionalUsageModal.FunctionalizationChanged',
      )
      .dispatch(functionalization)
  }

  /** Applies the functional usage to every selected record */
  public saveClickHandler(): void {
    this.bulkFunctionalUsagePlugin.actions.submit
      .from('Space.RecordsList.BulkFunctionalUsageModal.SaveButton.Clicked')
      .dispatch()
  }

  /** Closes the modal without applying anything */
  public cancelClickHandler(): void {
    this.bulkFunctionalUsagePlugin.actions.cancel
      .from('Space.RecordsList.BulkFunctionalUsageModal.CancelButton.Clicked')
      .dispatch()
  }
}
